import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiRefreshCw, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService } from '../api/services/dashboardService';
import { TestSessionDetail, PaginatedResponse, TableCountsResponse, GameTestRankItem } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

interface TestSession {
  id: number;
  playerId: string;
  completionStatus: string;
  averageResponseTime: number;
  resultCategory: string;
  startedAt: string;
  endedAt: string;
  startedAtOriginal: string | null;
  endedAtOriginal: string | null;
  durationInSeconds: number;
}

type SortColumn = 'id' | 'playerId' | 'completionStatus' | 'averageResponseTime' | 'resultCategory' | 'startedAt' | 'endedAt' | 'durationInSeconds';
type SortDirection = 'asc' | 'desc';

const TestSessions: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 25;
  const [tests, setTests] = useState<TestSession[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [tableCounts, setTableCounts] = useState<TableCountsResponse | null>(null);
  const [allTestSessionsForChart, setAllTestSessionsForChart] = useState<TestSessionDetail[]>([]);
  const [testRankData, setTestRankData] = useState<GameTestRankItem[]>([]);
  const [chartLoading, setChartLoading] = useState<boolean>(true);
  const [hiddenTests, setHiddenTests] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Fetch test sessions from API
  const fetchTestSessions = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response: PaginatedResponse<TestSessionDetail> = await dashboardService.getTestSessionDetails({
        page,
      });

      // Transform API data to local format
      const transformedTests: TestSession[] = response.data.map((testDetail) => ({
        id: testDetail.test_session_id,
        playerId: testDetail.player_id,
        completionStatus: testDetail.completion_status,
        averageResponseTime: testDetail.average_response_time,
        resultCategory: testDetail.result_category,
        startedAt: formatDate(testDetail.started_at),
        endedAt: formatDate(testDetail.ended_at),
        startedAtOriginal: testDetail.started_at,
        endedAtOriginal: testDetail.ended_at,
        durationInSeconds: testDetail.duration_in_seconds,
      }));

      setTests(transformedTests);
      setPagination({
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching test sessions:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch table counts for stats
  const fetchTableCounts = async () => {
    try {
      const response = await dashboardService.getTableCounts();
      setTableCounts(response);
    } catch (err: any) {
      console.error('Error fetching table counts:', err);
    }
  };

  // Fetch test sessions for chart (multiple pages) - DEPRECATED, using new API
  const fetchTestSessionsForChart = async () => {
    try {
      const allSessions: TestSessionDetail[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 10) { // Limit to 10 pages to avoid too many requests
        try {
          const response = await dashboardService.getTestSessionDetails({ page });
          allSessions.push(...response.data);
          hasMore = response.data.length > 0 && response.pagination.page < response.pagination.totalPages;
          page++;
        } catch (err) {
          console.error('Error fetching test sessions page for chart:', err);
          hasMore = false;
        }
      }
      
      setAllTestSessionsForChart(allSessions);
    } catch (err: any) {
      console.error('Error fetching test sessions for chart:', err);
    }
  };

  // Fetch test rank data for chart from new API
  const fetchTestRankData = async () => {
    try {
      setChartLoading(true);
      const response = await dashboardService.getGameTestRank({ type: 'tests' });
      setTestRankData(response);
    } catch (err: any) {
      console.error('Error fetching test rank data:', err);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchTestSessions(currentPage);
    fetchTableCounts();
    fetchTestRankData();
  }, [currentPage]);

  // Track if we've initialized hidden tests to prevent infinite loops
  const initializedTestsRef = useRef(false);

  // Initialize hidden tests: hide all except first 3
  useEffect(() => {
    if (testRankData.length > 0 && !initializedTestsRef.current) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const allTestIds = Array.from(new Set(testRankData
        .filter(item => {
          if (!item.started_at) return false;
          const itemDate = new Date(item.started_at);
          return itemDate <= today;
        })
        .map(item => String(item.id)) // Convert to string to match LineChart dataKey
      )).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      
      const testsToHide = allTestIds.slice(3); // Hide from 4th test onwards
      if (testsToHide.length > 0) {
        setHiddenTests(new Set(testsToHide));
        initializedTestsRef.current = true;
      }
    }
  }, [testRankData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchTestSessions(currentPage);
    fetchTableCounts();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  // Calculate percentage change
  const calculateChange = (current: number, previous: number): string => {
    if (previous === 0) return '+0%';
    const change = ((current - previous) / previous) * 100;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get daily change from metrics (daily growth percentage)
  const getDailyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get weekly change from metrics (weekly growth percentage)
  const getWeeklyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_weekly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get monthly change from metrics (monthly growth percentage)
  const getMonthlyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_monthly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get total today from table counts
  const getTotalToday = (): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === 'test_session');
    return count?.total_today || 0;
  };

  // Get average duration today from table counts
  const getAvgDurationToday = (): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === 'test_session');
    return count?.avg_duration_today || 0;
  };

  // Get daily change from Daily metrics (daily growth percentage)
  const getDailyValue = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Test ID to name mapping
  const getTestName = (testId: number | string): string => {
    const testIdNum = typeof testId === 'string' ? parseInt(testId, 10) : testId;
    const testNames: Record<number, string> = {
      0: 'Zeka Testi',
      1: 'Arketip',
      2: 'Kişilik',
      3: 'Bağlanma',
      4: 'Karar Stilleri',
      5: 'Duygu Analizi',
      6: 'Stres Profili',
      7: 'Zihinsel Zırhlar',
      8: 'Zihin Çemberi',
      9: 'Yaratıcılık',
      10: 'Takım Kimliği',
      11: 'Karanlık Üçlü',
      12: 'Sosyal Gerilim',
      13: 'Korku Tipleri',
      14: 'Güven Profili',
      15: 'Renk Üçgeni',
    };
    return testNames[testIdNum] || `Test ${testId}`;
  };

  // Format duration in seconds to readable format
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Handle sort
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort tests
  const sortedTests = useMemo(() => {
    if (!sortColumn) return tests;

    return [...tests].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'playerId':
          aValue = a.playerId.toLowerCase();
          bValue = b.playerId.toLowerCase();
          break;
        case 'completionStatus':
          aValue = a.completionStatus.toLowerCase();
          bValue = b.completionStatus.toLowerCase();
          break;
        case 'averageResponseTime':
          aValue = a.averageResponseTime;
          bValue = b.averageResponseTime;
          break;
        case 'resultCategory':
          aValue = a.resultCategory.toLowerCase();
          bValue = b.resultCategory.toLowerCase();
          break;
        case 'startedAt':
          aValue = a.startedAtOriginal ? new Date(a.startedAtOriginal).getTime() : 0;
          bValue = b.startedAtOriginal ? new Date(b.startedAtOriginal).getTime() : 0;
          break;
        case 'endedAt':
          aValue = a.endedAtOriginal ? new Date(a.endedAtOriginal).getTime() : 0;
          bValue = b.endedAtOriginal ? new Date(b.endedAtOriginal).getTime() : 0;
          break;
        case 'durationInSeconds':
          aValue = a.durationInSeconds;
          bValue = b.durationInSeconds;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tests, sortColumn, sortDirection]);

  const stats = [
    { 
      label: 'Toplam Test Oturumu', 
      value: pagination.total.toLocaleString('tr-TR'), 
      change: getDailyChange('test_session'), 
      weeklyChange: getWeeklyChange('test_session'),
      monthlyChange: getMonthlyChange('test_session'),
      color: 'blue' 
    },
    { 
      label: 'Test Oturumu', 
      value: getTotalToday().toLocaleString('tr-TR'), 
      color: 'green' 
    },
    { 
      label: 'Ortalama Süre', 
      value: formatDuration(getAvgDurationToday()), 
      color: 'orange' 
    },
  ];

  // Process test rank data for daily chart from new API
  const processTestSessionsChartData = useMemo(() => {
    if (testRankData.length === 0) return [];

    // Filter out future dates (only show dates up to today)
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    const filteredData = testRankData.filter(item => {
      if (!item.started_at) return false;
      const itemDate = new Date(item.started_at);
      return itemDate <= today;
    });

    if (filteredData.length === 0) return [];

    // Group by date and test id
    const dateTestGroups = filteredData.reduce((acc, item) => {
      if (!item.started_at) return acc;

      const date = item.started_at;
      const testId = String(item.id); // Convert to string for consistency

      if (!acc[date]) {
        acc[date] = {};
      }

      if (!acc[date][testId]) {
        acc[date][testId] = 0;
      }

      acc[date][testId] += item.count;

      return acc;
    }, {} as Record<string, Record<string, number>>);

    // Get all unique test IDs
    const allTestIds = Array.from(new Set(filteredData.map(item => String(item.id)))).sort((a, b) => {
      return parseInt(a, 10) - parseInt(b, 10);
    });

    // Get all unique dates and sort
    const dates = Object.keys(dateTestGroups).sort();

    // Build chart data - show all days
    return dates.map(date => {
      const dataPoint: any = {
        name: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
        date: date,
      };

      // Add count for each test (using test ID as key, but display name will be used)
      allTestIds.forEach(testId => {
        dataPoint[testId] = dateTestGroups[date]?.[testId] || 0;
      });

      return dataPoint;
    });
  }, [testRankData]);

  // API already handles pagination, so we show all tests from current page
  // No need for client-side slicing since API returns paginated data

  
    

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Test Oturumları
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`relative glass-strong rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 border overflow-hidden group transform hover:-translate-y-1 animate-fade-in ${
            stat.color === 'blue' ? 'hover:border-blue-300 dark:hover:border-blue-600' :
            stat.color === 'green' ? 'hover:border-green-300 dark:hover:border-green-600' :
            stat.color === 'orange' ? 'hover:border-orange-300 dark:hover:border-orange-600' :
            'hover:border-purple-300 dark:hover:border-purple-600'
          }`} style={{ animationDelay: `${index * 100}ms` }}>
            {/* Animated gradient background */}
            <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 group-hover:scale-150 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              stat.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' :
              'bg-purple-100 dark:bg-purple-900/30'
            }`}></div>
            <div className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              stat.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' :
              'bg-purple-100 dark:bg-purple-900/30'
            }`}></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 shimmer"></div>
            </div>
            
            <div className="relative z-10">
              <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">{stat.label}</p>
              <p className={`text-5xl font-extrabold mb-4 bg-gradient-to-r ${
                stat.color === 'blue' ? 'from-blue-600 via-blue-500 to-blue-400' :
                stat.color === 'green' ? 'from-green-600 via-green-500 to-green-400' :
                stat.color === 'orange' ? 'from-orange-600 via-orange-500 to-orange-400' :
                'from-purple-600 via-purple-500 to-purple-400'
              } bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
                {stat.value}
              </p>
              {stat.label === 'Toplam Test Oturumu' && (
                <div className="flex flex-col gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  {stat.change && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Günlük</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.change.startsWith('+') || stat.change.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.change}
                      </span>
                    </div>
                  )}
                  {stat.weeklyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Haftalık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.weeklyChange.startsWith('+') || stat.weeklyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.weeklyChange}
                      </span>
                    </div>
                  )}
                  {stat.monthlyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aylık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.monthlyChange.startsWith('+') || stat.monthlyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.monthlyChange}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Test Sessions Daily Duration Chart */}
      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-6 border transform hover:scale-[1.01] animate-fade-in">
        <h2 className="text-2xl font-bold gradient-text mb-6">Test Oturumları - Günlük Oynanma Sayısı ve Grafik</h2>
        {chartLoading ? (
          <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
            Yükleniyor...
          </div>
        ) : processTestSessionsChartData.length === 0 ? (
          <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
            Veri bulunamadı
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={processTestSessionsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'} />
              <XAxis 
                dataKey="name" 
                stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis 
                domain={[0, 'dataMax + 10']} 
                stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                label={{ value: 'Oturum Sayısı', angle: -90, position: 'insideLeft' }}
              />
            <Legend 
              wrapperStyle={{
                color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
                cursor: 'pointer'
              }}
              onClick={(e: any) => {
                const testId = e.dataKey;
                if (testId) {
                  setHiddenTests(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(testId)) {
                      newSet.delete(testId);
                    } else {
                      newSet.add(testId);
                    }
                    return newSet;
                  });
                }
              }}
              iconType="line"
              formatter={(value: string) => getTestName(value)}
            />
            {(() => {
              // Get all unique test IDs from filtered data
              const today = new Date();
              today.setHours(23, 59, 59, 999);
              const allTestIds = Array.from(new Set(testRankData
                .filter(item => {
                  if (!item.started_at) return false;
                  const itemDate = new Date(item.started_at);
                  return itemDate <= today;
                })
                .map(item => String(item.id))
              )).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

              return allTestIds.map((testId, index) => {
                const hue = (index * 137) % 360; // Use a prime number for better color distribution
                const color = `hsl(${hue}, 70%, 50%)`;
                const isHidden = hiddenTests.has(testId);
                
                return (
                  <Line 
                    key={testId}
                    type="monotone" 
                    dataKey={testId} 
                    stroke={color} 
                    strokeWidth={2} 
                    dot={{ r: 3 }} 
                    name={testId}
                    hide={isHidden}
                    strokeOpacity={isHidden ? 0 : 1}
                  />
                );
              });
            })()}
          </LineChart>
        </ResponsiveContainer>
        )}
      </div>

      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border transform hover:scale-[1.01] animate-fade-in">
        <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-indigo-50/50 via-purple-50/50 to-pink-50/50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm flex items-center justify-between">
          <h2 className="text-xl font-bold gradient-text">Test Oturumları</h2>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-800 dark:text-white bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 transform disabled:hover:scale-100"
          >
            {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
            <span>Yenile</span>
          </button>
        </div>
        {error && (
          <div className="px-6 py-4 glass bg-gradient-to-r from-red-50/80 to-rose-50/80 dark:from-red-900/30 dark:to-rose-900/30 border-b border-red-200/50 dark:border-red-800/50">
            <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
              <span className="text-xl">⚠️</span>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-2">
                    <span>ID</span>
                    {sortColumn === 'id' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('playerId')}
                >
                  <div className="flex items-center gap-2">
                    <span>Player ID</span>
                    {sortColumn === 'playerId' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('completionStatus')}
                >
                  <div className="flex items-center gap-2">
                    <span>Tamamlanma Durumu</span>
                    {sortColumn === 'completionStatus' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('averageResponseTime')}
                >
                  <div className="flex items-center gap-2">
                    <span>Ortalama Yanıt Süresi</span>
                    {sortColumn === 'averageResponseTime' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('resultCategory')}
                >
                  <div className="flex items-center gap-2">
                    <span>Sonuç Kategorisi</span>
                    {sortColumn === 'resultCategory' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('startedAt')}
                >
                  <div className="flex items-center gap-2">
                    <span>Başlangıç</span>
                    {sortColumn === 'startedAt' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('endedAt')}
                >
                  <div className="flex items-center gap-2">
                    <span>Bitiş</span>
                    {sortColumn === 'endedAt' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('durationInSeconds')}
                >
                  <div className="flex items-center gap-2">
                    <span>Süre (s)</span>
                    {sortColumn === 'durationInSeconds' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {sortedTests.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                      Test oturumu bulunamadı
                    </td>
                  </tr>
                ) : (
                  sortedTests.map((test) => (
                <tr key={test.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">{test.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300 font-mono text-xs">{test.playerId}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      test.completionStatus === 'COMPLETED' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                        : test.completionStatus === 'SKIPPED'
                        ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}>
                      {test.completionStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{test.averageResponseTime}s</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{test.resultCategory}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{test.startedAt}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{test.endedAt}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900 dark:text-white">{test.durationInSeconds}s</td>
                </tr>
                  ))
                )}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan {tests.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-{Math.min(currentPage * itemsPerPage, pagination.total)} arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Önceki
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 10) {
                    pageNum = i + 1;
                  } else if (currentPage <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 4) {
                    pageNum = pagination.totalPages - 9 + i;
                  } else {
                    pageNum = currentPage - 5 + i;
                  }
                  return (
                  <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 ${
                        currentPage === pageNum
                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 text-white shadow-lg scale-110'
                        : 'text-slate-700 dark:text-slate-300 glass border border-gray-300/50 dark:border-gray-600/50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 hover:scale-105 transform shadow-sm hover:shadow-md'
                    }`}
                  >
                      {pageNum}
                  </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestSessions;

